import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';

const ManagerDashboard = () => {
  const [activeTab, setActiveTab] = useState('users');

  const tabs = [
    { id: 'users', label: 'User Monitoring', icon: 'Users' },
    { id: 'transactions', label: 'Transaction Oversight', icon: 'DollarSign' },
    { id: 'reports', label: 'Operational Reports', icon: 'FileText' },
    { id: 'kyc', label: 'KYC Review', icon: 'UserCheck' },
    { id: 'monitoring', label: 'System Monitoring', icon: 'Activity' }
  ];

  // Mock data for demonstration
  const userStats = {
    totalUsers: 2847,
    activeToday: 456,
    pendingVerification: 23,
    recentActivity: 127
  };

  const transactionStats = {
    todayVolume: '₹12.4L',
    pendingDisputes: 5,
    completedBookings: 89,
    escrowHeld: '₹8.2L'
  };

  const renderUserMonitoring = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <Icon name="Users" size={24} className="text-blue-600" />
            </div>
            <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">+12%</span>
          </div>
          <h3 className="text-2xl font-bold text-foreground">{userStats?.totalUsers}</h3>
          <p className="text-sm text-muted-foreground">Total Active Users</p>
        </div>

        <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <Icon name="CheckCircle" size={24} className="text-green-600" />
            </div>
            <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded">Today</span>
          </div>
          <h3 className="text-2xl font-bold text-foreground">{userStats?.activeToday}</h3>
          <p className="text-sm text-muted-foreground">Active Users Today</p>
        </div>

        <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
              <Icon name="Clock" size={24} className="text-yellow-600" />
            </div>
            <span className="text-xs font-medium text-yellow-600 bg-yellow-100 px-2 py-1 rounded">Pending</span>
          </div>
          <h3 className="text-2xl font-bold text-foreground">{userStats?.pendingVerification}</h3>
          <p className="text-sm text-muted-foreground">Pending Verification</p>
        </div>

        <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
              <Icon name="TrendingUp" size={24} className="text-purple-600" />
            </div>
            <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-1 rounded">Live</span>
          </div>
          <h3 className="text-2xl font-bold text-foreground">{userStats?.recentActivity}</h3>
          <p className="text-sm text-muted-foreground">Recent Activities</p>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Users" size={20} className="text-[#ff8c00]" />
          Recent User Activities
        </h3>
        <div className="space-y-3">
          {[1, 2, 3, 4]?.map((item) => (
            <div key={item} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1e4a72] to-[#ff8c00] flex items-center justify-center text-white font-semibold">
                  U{item}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">User {item} - Account Update</p>
                  <p className="text-xs text-muted-foreground">Profile verification completed</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">2 mins ago</span>
                <button className="px-3 py-1 text-xs font-medium text-[#1e4a72] bg-blue-50 rounded hover:bg-blue-100 transition-colors">
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1">
          <Icon name="AlertCircle" size={14} />
          Note: User deletion is restricted to full admin access only
        </p>
      </div>
    </div>
  );

  const renderTransactionOversight = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <Icon name="DollarSign" size={24} className="text-green-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-foreground">{transactionStats?.todayVolume}</h3>
          <p className="text-sm text-muted-foreground">Today's Transaction Volume</p>
        </div>

        <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
              <Icon name="AlertCircle" size={24} className="text-red-600" />
            </div>
            <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded">Urgent</span>
          </div>
          <h3 className="text-2xl font-bold text-foreground">{transactionStats?.pendingDisputes}</h3>
          <p className="text-sm text-muted-foreground">Pending Disputes</p>
        </div>

        <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <Icon name="CheckCircle2" size={24} className="text-blue-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-foreground">{transactionStats?.completedBookings}</h3>
          <p className="text-sm text-muted-foreground">Completed Bookings</p>
        </div>

        <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
              <Icon name="Lock" size={24} className="text-yellow-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-foreground">{transactionStats?.escrowHeld}</h3>
          <p className="text-sm text-muted-foreground">Secured Amount Held</p>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="DollarSign" size={20} className="text-[#ff8c00]" />
          Recent Transactions (Read-Only)
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Transaction ID</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Type</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Amount</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Action</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5]?.map((item) => (
                <tr key={item} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4 text-sm text-foreground">TXN{1000 + item}</td>
                  <td className="py-3 px-4 text-sm text-foreground">Sugar Purchase</td>
                  <td className="py-3 px-4 text-sm font-semibold text-green-600">₹{(Math.random() * 100000)?.toFixed(0)}</td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-600 rounded">Completed</span>
                  </td>
                  <td className="py-3 px-4">
                    <button className="text-xs font-medium text-[#1e4a72] hover:text-[#ff8c00] transition-colors">
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1">
          <Icon name="Info" size={14} />
          Financial modifications require full admin authorization
        </p>
      </div>
    </div>
  );

  const renderOperationalReports = () => (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="FileText" size={20} className="text-[#ff8c00]" />
          Daily Operational Reports
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { title: 'Daily User Activity', icon: 'Users', color: 'blue' },
            { title: 'Transaction Summary', icon: 'DollarSign', color: 'green' },
            { title: 'Booking Analytics', icon: 'TrendingUp', color: 'purple' },
            { title: 'Performance Metrics', icon: 'BarChart3', color: 'orange' }
          ]?.map((report, idx) => (
            <div key={idx} className="p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors cursor-pointer">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg bg-${report?.color}-100 flex items-center justify-center`}>
                  <Icon name={report?.icon} size={20} className={`text-${report?.color}-600`} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{report?.title}</p>
                  <p className="text-xs text-muted-foreground">Generated today</p>
                </div>
              </div>
              <button className="w-full px-4 py-2 text-sm font-medium text-[#1e4a72] bg-blue-50 rounded hover:bg-blue-100 transition-colors">
                Download Report
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Schedule Automated Reports</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              <Icon name="Calendar" size={20} className="text-[#ff8c00]" />
              <div>
                <p className="text-sm font-medium text-foreground">Weekly Performance Summary</p>
                <p className="text-xs text-muted-foreground">Sent every Monday at 9:00 AM</p>
              </div>
            </div>
            <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-600 rounded">Active</span>
          </div>
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              <Icon name="Calendar" size={20} className="text-[#ff8c00]" />
              <div>
                <p className="text-sm font-medium text-foreground">Monthly Analytics Report</p>
                <p className="text-xs text-muted-foreground">Sent 1st of each month</p>
              </div>
            </div>
            <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-600 rounded">Active</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderKYCReview = () => (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="UserCheck" size={20} className="text-[#ff8c00]" />
          Pending KYC Verifications
        </h3>
        <div className="space-y-3">
          {[1, 2, 3]?.map((item) => (
            <div key={item} className="p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1e4a72] to-[#ff8c00] flex items-center justify-center text-white font-semibold">
                    U{item}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">User {item} - Buyer Account</p>
                    <p className="text-xs text-muted-foreground">Submitted 2 days ago</p>
                  </div>
                </div>
                <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-600 rounded">Pending Review</span>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="text-xs">
                  <span className="text-muted-foreground">Aadhaar:</span>
                  <span className="ml-1 text-foreground font-medium">Uploaded</span>
                </div>
                <div className="text-xs">
                  <span className="text-muted-foreground">PAN:</span>
                  <span className="ml-1 text-foreground font-medium">Uploaded</span>
                </div>
                <div className="text-xs">
                  <span className="text-muted-foreground">GST:</span>
                  <span className="ml-1 text-foreground font-medium">Uploaded</span>
                </div>
                <div className="text-xs">
                  <span className="text-muted-foreground">Bank:</span>
                  <span className="ml-1 text-foreground font-medium">Verified</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button className="flex-1 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-500 rounded hover:opacity-90 transition-opacity">
                  Approve Documents
                </button>
                <button className="px-4 py-2 text-sm font-medium text-[#1e4a72] bg-blue-50 rounded hover:bg-blue-100 transition-colors">
                  View Details
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                Final rejection requires admin approval
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSystemMonitoring = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <Icon name="Activity" size={24} className="text-green-600" />
            </div>
            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-600 rounded">Healthy</span>
          </div>
          <h3 className="text-2xl font-bold text-foreground">99.8%</h3>
          <p className="text-sm text-muted-foreground">System Uptime</p>
        </div>

        <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <Icon name="Zap" size={24} className="text-blue-600" />
            </div>
            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-600 rounded">Good</span>
          </div>
          <h3 className="text-2xl font-bold text-foreground">1.2s</h3>
          <p className="text-sm text-muted-foreground">Avg Response Time</p>
        </div>

        <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
              <Icon name="AlertTriangle" size={24} className="text-yellow-600" />
            </div>
            <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-600 rounded">2 Alerts</span>
          </div>
          <h3 className="text-2xl font-bold text-foreground">2</h3>
          <p className="text-sm text-muted-foreground">Active Alerts</p>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Activity" size={20} className="text-[#ff8c00]" />
          System Health Indicators (View-Only)
        </h3>
        <div className="space-y-4">
          {[
            { name: 'API Response Time', status: 'good', value: '245ms', color: 'green' },
            { name: 'Database Performance', status: 'good', value: '98%', color: 'green' },
            { name: 'Storage Usage', status: 'warning', value: '78%', color: 'yellow' },
            { name: 'Active Sessions', status: 'good', value: '456', color: 'blue' }
          ]?.map((indicator, idx) => (
            <div key={idx} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full bg-${indicator?.color}-500`}></div>
                <p className="text-sm font-medium text-foreground">{indicator?.name}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-foreground">{indicator?.value}</span>
                <span className={`px-2 py-1 text-xs font-medium bg-${indicator?.color}-100 text-${indicator?.color}-600 rounded capitalize`}>
                  {indicator?.status}
                </span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1">
          <Icon name="Lock" size={14} />
          System configuration changes require full admin access
        </p>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch(activeTab) {
      case 'users':
        return renderUserMonitoring();
      case 'transactions':
        return renderTransactionOversight();
      case 'reports':
        return renderOperationalReports();
      case 'kyc':
        return renderKYCReview();
      case 'monitoring':
        return renderSystemMonitoring();
      default:
        return renderUserMonitoring();
    }
  };

  return (
    <>
      <Helmet>
        <title>Manager Dashboard - SugarHathi | Operational Management</title>
        <meta name="description" content="Manager dashboard for SugarHathi platform operational oversight with limited administrative capabilities. Monitor users, transactions, KYC, and system health." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <RoleBasedHeader />
        <BreadcrumbNavigation />
        <main className="main-content">
          <div className="max-w-[1600px] mx-auto px-6 py-8">
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#1e4a72] to-[#ff8c00] flex items-center justify-center">
                    <Icon name="Shield" size={24} color="#FFFFFF" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-foreground">Manager Dashboard</h1>
                    <p className="text-muted-foreground">Operational oversight with limited privileges</p>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg border border-blue-100">
                  <Icon name="Info" size={16} className="text-[#1e4a72]" />
                  <span className="text-sm text-[#1e4a72] font-medium">Sub-Admin Access</span>
                </div>
              </div>
            </div>

            {/* Desktop Tabs */}
            <div className="hidden md:block bg-card rounded-lg border border-border mb-6">
              <div className="flex items-center gap-2 p-2 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white shadow-lg'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile Tabs - Dropdown Style */}
            <div className="md:hidden mb-6">
              <div className="relative">
                <select
                  value={activeTab}
                  onChange={(e) => setActiveTab(e?.target?.value)}
                  className="w-full px-4 py-3 bg-card border border-border rounded-lg text-foreground font-medium appearance-none cursor-pointer focus:ring-2 focus:ring-[#ff8c00] focus:border-transparent"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.5rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em',
                    paddingRight: '2.5rem'
                  }}
                >
                  {tabs?.map((tab) => (
                    <option key={tab?.id} value={tab?.id}>
                      {tab?.label}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <Icon 
                    name={tabs?.find(t => t?.id === activeTab)?.icon || 'Shield'} 
                    size={18} 
                    className="text-[#ff8c00]"
                  />
                </div>
              </div>
            </div>

            <div className="transition-all duration-300">
              {renderTabContent()}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default ManagerDashboard;